import { Button, Modal, message, notification } from "antd";
import { useTranslation } from "react-i18next";
import { ApiService, ViewRequestApprover } from "../api";
import ButtonGroup from "antd/es/button/button-group";
import { NCALayerClient } from "ncalayer-js-client";
import { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import ReactSignatureCanvas from "react-signature-canvas";
import { NotificationType, openNotificationWithIcon } from "../constants";

const userInfo = localStorage.getItem("userInfo")
  ? JSON.parse(localStorage.getItem("userInfo") || "{}")
  : null;

type ActionColumnProps = {
  onApprove: () => Promise<void>;
  onReject: () => Promise<void>;
};

type EhsActionColumnProps = {
  onApprove: (sign: string | null) => Promise<void>;
  onReject: () => Promise<void>;
};

const DigitalSignActionColumn = (props: ActionColumnProps) => {
  const { t } = useTranslation();
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const handleApprove = async () => {
    setApproving(true);
    await props.onApprove();
    setApproving(false);
  };

  const handleReject = async () => {
    setRejecting(true);
    await props.onReject();
    setRejecting(false);
  };

  return (
    <ButtonGroup>
      <Button
        type="primary"
        loading={approving}
        disabled={rejecting}
        onClick={() => handleApprove()}
      >
        {t("Approve by EDS")}
      </Button>
      <Button
        loading={rejecting}
        disabled={approving}
        onClick={() => handleReject()}
        danger
      >
        {t("Reject")}
      </Button>
    </ButtonGroup>
  );
};

const ButtonActionColumn = (props: ActionColumnProps) => {
  const { t } = useTranslation();
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const handleApprove = async () => {
    setApproving(true);
    await props.onApprove();
    setApproving(false);
  };

  const handleReject = async () => {
    setRejecting(true);
    await props.onReject();
    setRejecting(false);
  };

  return (
    <ButtonGroup>
      <Button
        type="primary"
        loading={approving}
        disabled={rejecting}
        onClick={() => handleApprove()}
      >
        {t("Approve")}
      </Button>
      <Button
        loading={rejecting}
        disabled={approving}
        onClick={() => handleReject()}
        danger
      >
        {t("Reject")}
      </Button>
    </ButtonGroup>
  );
};

const EHSActionColumn = (props: EhsActionColumnProps) => {
  const { t } = useTranslation();
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [visible, setVisible] = useState(false);
  const sigCanvas = useRef<ReactSignatureCanvas>(null);

  const handleSignClear = () => {
    if (sigCanvas === null) {
      return;
    }
    sigCanvas.current!.clear();
  };

  const handleApprove = async () => {
    if (sigCanvas === null) {
      return;
    }
    setApproving(true);
    const sign = sigCanvas.current!.getTrimmedCanvas().toDataURL("image/png");
    console.log(sign);
    await props.onApprove(sign);
    setApproving(false);
  };

  const handleReject = async () => {
    setRejecting(true);
    await props.onReject();
    setRejecting(false);
  };

  const handleOpenModal = () => {
    setVisible(true);
  };

  const handleCancelModal = () => {
    setVisible(false);
  };

  return (
    <div>
      <ButtonGroup>
        <Button
          type="primary"
          loading={approving}
          disabled={rejecting}
          onClick={() => handleOpenModal()}
        >
          {t("Approve by EHS")}
        </Button>
        <Button
          loading={rejecting}
          disabled={approving}
          onClick={() => handleReject()}
          danger
        >
          {t("Reject")}
        </Button>
        <Modal
          title={t("EHS sign")}
          open={visible}
          onOk={handleApprove}
          confirmLoading={approving}
          onCancel={handleCancelModal}
          footer={[
            <Button key="clear" onClick={handleSignClear}>
              {t("Clear")}
            </Button>,
            <Button key="cancel" onClick={handleCancelModal}>
              {t("Cancel")}
            </Button>,
            <Button key="ok" type="primary" onClick={handleApprove}>
              {t("Sign")}
            </Button>,
          ]}
        >
          <SignatureCanvas
            ref={sigCanvas}
            penColor="black"
            canvasProps={{
              width: 468,
              height: 200,
              className: "sigCanvas",
              style: {
                border: "0.5px solid black",
                borderRadius: "5px",
                borderColor: "grey",
              },
            }}
          />
        </Modal>
      </ButtonGroup>
    </div>
  );
};

type ApprovalButtonsColumnProps = {
  record: ViewRequestApprover;
  requestId?: string;
  workflowId: number;
  fetch: number;
  setFetch: (fetch: number) => void;
};

const ApprovalButtonsColumn = (props: ApprovalButtonsColumnProps) => {
  const { t } = useTranslation();
  const canSeeApprovalButtons =
    userInfo.id === props.record.user_id && props.record.status === "Started";

  if (!canSeeApprovalButtons) {
    return null;
  }

  const handleApprove = async (sign: string | null) => {
    try {
      await ApiService.apiMainRequestWorkflowApproveCreate({
        request_id: parseInt(props.requestId!),
        workflow_id: props.workflowId,
        user_id: userInfo.id,
        sign: sign,
        // Names the slot being signed. One user may hold several roles in a
        // stage, and each row here is one of them — without this the server
        // cannot tell which of their signatures this click is for.
        role_id: props.record.role_id,
      });

      openNotificationWithIcon(
        "success",
        t("Request has been successfully approved")
      );
      props.setFetch(props.fetch + 1);
    } catch (e) {
      openNotificationWithIcon("error");
    }
  };

  const handleButtonApprove = async () => {
    await handleApprove(null);
  };

  const handleEhsApprove = async (sign: string | null) => {
    await handleApprove(sign);
  };

  const handleDigitalSignApprover = async () => {
    const ncalayerClient = new NCALayerClient();

    try {
      await ncalayerClient.connect();
    } catch (error: any) {
      alert(`${t("Failed to connect to NCALayer:")} ${error.toString()}`);
      return;
    }

    let base64EncodedSignature;

    const fileData = await ApiService.apiMainGetRequestDocRetrieve(
      parseInt(props.requestId!)
    );

    try {
      base64EncodedSignature = await ncalayerClient.basicsSignCMS(
        NCALayerClient.basicsStoragesAll,
        fileData,
        NCALayerClient.basicsCMSParamsAttached,
        NCALayerClient.basicsSignerSignAny
      );
    } catch (error: any) {
      if (error.canceledByUser) {
        alert(t("Action cancelled by user."));
      }

      alert(error.toString());
      return;
    }

    const signature = Array.isArray(base64EncodedSignature)
      ? base64EncodedSignature[0]
      : base64EncodedSignature;
    await handleApprove(signature);

    return signature;
  };

  const handleReject = async () => {
    try {
      await ApiService.apiMainRequestWorkflowRejectCreate({
        request_id: parseInt(props.requestId!),
        workflow_id: props.workflowId,
        user_id: userInfo.id,
        sign: null,
        role_id: props.record.role_id,
      });

      openNotificationWithIcon(
        "success",
        t("Request has been successfully rejected")
      );

      props.setFetch(props.fetch + 1);
    } catch (e) {
      openNotificationWithIcon("error");
    }
  };

  switch (props.record.type) {
    case "Button":
      return (
        <ButtonActionColumn
          onApprove={handleButtonApprove}
          onReject={handleReject}
        />
      );
    case "EDS":
      return (
        <DigitalSignActionColumn
          onApprove={handleDigitalSignApprover}
          onReject={handleReject}
        />
      );
    case "EHS":
      return (
        <EHSActionColumn onApprove={handleEhsApprove} onReject={handleReject} />
      );
    default:
      return null;
  }
};

export default ApprovalButtonsColumn;
